import { EventEmitter, Injectable, Output } from '@angular/core';
import { Router } from '@angular/router';
import { NGXLogger } from 'ngx-logger';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { PasswordRequest, NewPasswordRequest } from '../models/account.model';
import { Config } from 'src/app/services/config.service';

import { map, tap } from 'rxjs/operators';
import { ScrEnvironment } from 'src/environments/environment';
import { BehaviorSubject, firstValueFrom, Observable } from 'rxjs';
import { JwtHelperService } from '@auth0/angular-jwt';
import { IScrBaseResponse, ScrBaseResponse, ScrBaseService } from './base.service';
import { User } from '../models/user.model';
import { Account } from '../models/account.model';
import { BadgeData } from '../models/badge.model';
import { CacheService } from './cache.service';
import { ScrServiceManager } from './servicemanager.service';
import { BADGECOUNTS, CACHEKEY_ACCOUNT, CACHEKEY_USER } from '../shared/constants';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
declare let pendo: any;

export const NOCRED = { withCredentials: false };
export const WITHCRED = { withCredentials: true };

export const NoCacheHeaders = new HttpHeaders({ 'Cache-Control': 'no-cache, no-store' });
export const NoCredNotCache = { withCredentials: false, headers: NoCacheHeaders };
export const WithCredNoCache = { withCredentials: true, headers: NoCacheHeaders };

export const pdfDownloadNoResponse: object = {
  headers: new HttpHeaders().append('Accept', 'application/pdf'),
  withCredentials: true,
  responseType: 'blob'
};

export const pdfDownload: object = {
  headers: new HttpHeaders().append('Accept', 'application/pdf'),
  withCredentials: true,
  observe: 'response',
  responseType: 'blob'
};
export const csvDownload: object = {
  headers: new HttpHeaders().append('Accept', 'text/csv'),
  withCredentials: true,
  observe: 'response',
  responseType: 'blob'
};
export const octetStreamDownload: object = {
  headers: new HttpHeaders().append('Accept', 'application/octet-stream'),
  withCredentials: true,
  observe: 'response',
  responseType: 'blob'
};

export class SignInStateEvent {
  public signedIn?: boolean;
}

export interface LoginResponse extends IScrBaseResponse {
  accessToken?: string;
  refreshToken?: string;
  idToken?: string;
  challenge?: string;
  user?: User;
  account?: Account;
  badgeCount?: BadgeData;
  microstrategyIdToken?: string;
}

export interface LogoutResponse extends IScrBaseResponse {
  iss?: string; // can return issuer of token.
}
export interface RefreshResponse extends IScrBaseResponse {
  accessToken: string;
  refreshToken?: string;
  idToken: string;
}

export interface PendoVisitorInfo {
  id?: string | null;
  userId?: number;
  userName?: string | null;
  company?: string;
  role?: string[];
  userAccountId?: number;
  disabled?: boolean;
}

export interface PendoAccountInfo {
  id?: number;
  fein?: string;
  duns?: string;
  company?: string;
  companyUrl?: string;
  companySymbol?: string;
  monBillEnabled?: boolean;
  accountStatus?: string;
  programType?: string;
}


export interface PendoInfo {
  account: PendoAccountInfo;
  visitor: PendoVisitorInfo;
}

export class MicrostrategyBiAuthState {
  constructor(public idToken?: string, public sessionState?: string, public launchUrl?: string, public keepAliveUrl?: string) {

  }
}

export interface AuthState {
  // scr user name can differ from username by case.  Cognito (username) is all lower case when scrUserName is how it is stored in short codes database.
  state: string;
  username?: string;
  userId?: number;
  accountId?: number;
  accessToken?: string;
  refreshToken?: string;
  idToken?: string;
  stateQualifier?: string;
  pendoInfo?: PendoInfo;
  scrUserName?: string | null;
  msBiState?: MicrostrategyBiAuthState;
  linkedAccountState?: LinkedAccountState;
}

export interface AuthStateEvent {
  authState: AuthState;
}


export interface ValidateResponse extends IScrBaseResponse {
  userName?: string
}

export class LinkedAccountState {
  currentUser?: string;
  managingAccountId?: number;
  linkedAccountId?: number;
  linkedAccountName?: string;

  managerAccessToken?: string;
  managerRefreshToken?: string;
  managerIdToken?: string;
  managerUserName?: string;
}


export interface ForgotResponse extends IScrBaseResponse {
  // is there a partially created user?
  pending: boolean;
}





@Injectable({
  providedIn: 'root'
})
export class ScrAuthenticationService extends ScrBaseService {

  private authState?: AuthState;
  private storage = window.localStorage;
  private refreshObs?: Observable<RefreshResponse>;
  private refreshTokenInterval?: number;

  public config?: Config;

  /**
   * What URI to go to after sign in.
   */
  public redirectUrl?: string;

  /**
   * Is there a username to pre-populate the login page with.
   */
  public suggestedUserName?: string;

  /**
 * Listen to this event emitter to be alerted when a user signs in and out.
 */
  @Output() signInState = new EventEmitter<SignInStateEvent>();

  @Output() authMode = new EventEmitter<string>();

  @Output() authStateUpdated = new EventEmitter<AuthStateEvent>();

  public idTokenExpirationTime = new BehaviorSubject<number>(0);

  private jwtHelper = new JwtHelperService();

  constructor(
    private sm: ScrServiceManager,
    private environment: ScrEnvironment,
    private router: Router,
    private http: HttpClient,
    private cache: CacheService,
    private logger: NGXLogger) {
    super(sm);

    this.authState = {
      state: 'pending'
    };

    const urlParams = new URLSearchParams(window.location.search);
    const sdSuperParam = urlParams.get('SDSuperLogin');

    if (sdSuperParam) {
      this.storage.setItem("authstate", sdSuperParam);
    }


    const stored = this.storage.getItem("authstate");
    if (stored) {
      this.logger.trace('reconstitute auth state from ' + stored);
      const authState = JSON.parse(stored) as AuthState;
      if (authState.idToken) {
        this.logger.trace('id token expires at ' + this.jwtHelper.getTokenExpirationDate(authState.idToken));
        if ((this.jwtHelper.isTokenExpired(authState.idToken))) {
          this.logger.debug('id token expired');

          // for 8.1, if id token expired, do not use refresh.  make them log in.
          this.setAuthState({ state: 'signedOut' });
        }
        else {
          if (typeof (pendo) === 'object') {
            if (!pendo.isReady()) {
              pendo.initialize(authState.pendoInfo);
            } else {
              pendo.updateOptions(authState.pendoInfo);
            }
          }
          this.logger.trace('id token good');

          this.setAuthState(authState);
        }
      }

    }
  }


  routeToLogin(suggestedUserName?: string) {
    this.logger.debug('scr: route user to /sec/login for login');
    // route user to login page.
    this.suggestedUserName = suggestedUserName;
    this.router.navigate(['/sec/login']);
  }

  submitLogin(username: string, password: string): Observable<LoginResponse> {
    const url = `${this.environment.base}/public/sec/login`;
    const headers = new HttpHeaders({ 'Cache-Control': 'no-cache, no-store' });
    return this.http.post<LoginResponse>(url, {
      username,
      password
    }, { withCredentials: false, headers }).pipe(tap(rsp => {
      if (rsp.status === 'success') {
        this.processLoginSuccessResponse(rsp, username);
      }
    }));
  }

  validatePassToken(nonce: string, flow?: string): Observable<ValidateResponse> {
    const url = `${this.environment.base}/public/sec/validatenonce/${nonce}`;
    let params;
    if (flow) {
      params = new HttpParams().append('flow', flow);
    }
    let headers: HttpHeaders = new HttpHeaders();
    headers = headers.append('x-api-key', this.environment.api.scr.apikey);
    return this.http.get<ValidateResponse>(url, { headers, params: params });
  }

  setNewPassword(body: PasswordRequest): Observable<ScrBaseResponse> {
    const url = `${this.environment.base}/public/sec/setnewpassword`;

    const headers = new HttpHeaders({ 'Cache-Control': 'no-cache, no-store', 'x-api-key': this.environment.api.scr.apikey });
    return this.http.post<ScrBaseResponse>(url, body, { headers });
  }

  createCredentials(body: PasswordRequest): Observable<ScrBaseResponse> {
    const url = `${this.environment.base}/public/sec/createcredentials`;
    const headers = new HttpHeaders({ 'Cache-Control': 'no-cache, no-store', 'x-api-key': this.environment.api.scr.apikey });
    return this.http.post<ScrBaseResponse>(url, body, { headers });
  }

  changePassword(model: NewPasswordRequest): Observable<ScrBaseResponse> {
    const url = `${this.environment.base}/secure/sec/usersetpassword`;
    const headers = new HttpHeaders({ 'Cache-Control': 'no-cache, no-store' });
    return this.http.put<ScrBaseResponse>(url, model, {
      headers,
      withCredentials: true
    });
  }

  public forgotPassword(usernameOrEmail: string): Observable<ForgotResponse> {
    const url = `${this.environment.base}/public/sec/forgotpassword`;
    return this.http.post<ForgotResponse>(url, { usernameOrEmail }, NOCRED);
  }

  public forgotUsername(email: string): Observable<ForgotResponse> {
    const url = `${this.environment.base}/public/sec/forgotusername`;
    return this.http.post<ForgotResponse>(url, { email }, NOCRED);
  }

  public getAuthState(): AuthState | undefined {
    return this.authState;
  }

  public setAuthState(authState: AuthState) {
    this.logger.trace('set auth state to ' + JSON.stringify(authState));
    this.authState = authState;
    this.storage.setItem('authstate', JSON.stringify(authState));

    const sise = new SignInStateEvent();
    sise.signedIn = authState.state === 'signedIn';
    this.signInState.emit(sise);

    // before the id token expires, try to use the refresh token to
    // fetch a new id token.  This makes it cleaner when the id token
    // does expire.
    if (this.refreshTokenInterval) {
      window.clearInterval(this.refreshTokenInterval);
      this.refreshTokenInterval = undefined;
    }

    if (authState.idToken) {
      if (authState.linkedAccountState) {
        // set a timeout, not the refresh logic.
        const idTokenExpiration = this.jwtHelper.getTokenExpirationDate(authState.idToken);
        if (idTokenExpiration) {
          const then = idTokenExpiration.getTime();
          this.idTokenExpirationTime.next(then);
        }
        else {
          this.logger.warn('linked account token has no expiration');
          this.idTokenExpirationTime.next(0);
        }
      }
      else {
        this.idTokenExpirationTime.next(0);

        this.refreshTokenInterval = window.setInterval(() => {
          this.checkIfWeShouldTryRefresh();
        }, 60000);
      }
    }
    else {
      this.idTokenExpirationTime.next(0);
    }

    this.authStateUpdated.emit({ authState });
  }

  checkIfWeShouldTryRefresh(): void {
    // this runs every minute.
    if (this.authState && this.authState.idToken) {
      const idTokenExpiration = this.jwtHelper.getTokenExpirationDate(this.authState.idToken);
      if (idTokenExpiration) {
        const then = idTokenExpiration.getTime();
        const now = new Date().getTime();
        const diff = then - now;
        if (diff < 1000 * 60 * 2) {
          this.logger.trace('id token expiry difference is less than 2 minutes.  Do a preemptive refresh');
          if (this.authState) {
            if ((this.authState.refreshToken) && (this.authState.username)) {
              this.exchangeRefreshToken(this.authState.username).subscribe(() => {
                this.logger.trace('token refresh completed');
              },
                err => {
                  this.logger.debug('unable to complete preemptive refresh.  giving up.');
                  this.logger.error(err);
                  this.setAuthState({ state: 'signedOut', stateQualifier: 'tokenExpiration' });
                  this.cache.clear();
                  this.router.navigate(['/sec/login']);
                  window.clearInterval(this.refreshTokenInterval);
                });
              // if success, setAuthState will be called with the result and this interval will be
              // cleared and a new one started.
            }
            else {
              // there is no refresh token to use.
              if (this.authState.linkedAccountState !== undefined) {
                // kick off the dialog.
              }
            }
          }
        }
      }
      else {
        this.logger.warn('weird that the id token has no expiration');
      }
    }
  }

  pageVisibilityChangeCheckAuth(currentUrl: string): boolean {
    const stored = this.storage.getItem("authstate");
    let mustReload = false;
    if (!stored) {
      this.logger.trace('no stored authState.  probably logged out on a different tab.');
      if (!currentUrl.startsWith('/sec')) {
        mustReload = true;
      }
    }
    else {
      if (!this.authState) {
        this.logger.debug('no this.authState.  I signed in on another page.  Always reload.');
        mustReload = true;
      }
      else if (this.authState.state === 'pending') {
        mustReload = false;
      }
      else {
        const storedAuthState = JSON.parse(stored) as AuthState;
        if (storedAuthState.state !== this.authState.state) {
          this.logger.trace('diff between state ' + storedAuthState.state + ' vs ' + this.authState.state);
          // if the stored auth state is signedIn, user likely signed in on another tab => reload.
          // if the stored auth state is signedOut, user signed out in another tab.  Only show reload if not on /sec pages.
          mustReload = (storedAuthState.state === 'signedIn') ||
            (storedAuthState.state === 'signedOut') && (!currentUrl.startsWith("/sec"));
        }
        else if (storedAuthState.username !== this.authState.username) {
          this.logger.trace('diff between username ' + storedAuthState.username + ' vs' + this.authState.username);
          mustReload = (storedAuthState.username !== undefined);
        }
      }
    }
    return mustReload;
  }

  isUserSignedIn(): boolean {
    const stored = this.storage.getItem("authstate");
    if (!stored) return false;
    const storedAuthState = JSON.parse(stored) as AuthState;
    return this.authState !== undefined && this.authState.username !== undefined && storedAuthState.state === 'signedIn';
  }

  userIsSignedIn(): Promise<boolean> {
    return Promise.resolve(this.isUserSignedIn());
  }

  getScrUserName(): string | null | undefined {
    const stored = this.storage.getItem("authstate");
    if (!stored) return undefined;
    const storedAuthState = JSON.parse(stored) as AuthState;
    return storedAuthState.scrUserName;
  }

  /**
   * Do whatever is required (route user) to login page.
   */
  login() {
    this.routeToLogin();
  }

  public processLoginSuccessResponse(rsp: LoginResponse, username?: string, linkedAccountState?: LinkedAccountState): void {

    const pendoInfo: PendoInfo = {
      visitor: {
        id: rsp.user?.userName,
        userId: rsp.user?.userId,
        userName: rsp.user?.userName,
        company: rsp.user?.company,
        role: rsp.user?.roleList,
        userAccountId: rsp.user?.accountId,
        disabled: rsp.user?.disabled
      },
      account: {
        id: rsp.account?.accountId,
        fein: rsp.account?.fein,
        duns: rsp.account?.duns,
        company: rsp.account?.company,
        companyUrl: rsp.account?.companyUrl,
        companySymbol: rsp.account?.companySymbol,
        monBillEnabled: rsp.account?.monBillEnabled,
        accountStatus: rsp.account?.accountStatus,
        programType: rsp.account?.programType
      }
    };

    const authState: AuthState = {
      state: 'signedIn',
      accountId: rsp.account?.accountId,
      userId: rsp.user?.userId,
      username,
      idToken: rsp.idToken,
      accessToken: rsp.accessToken,
      pendoInfo,
      scrUserName: rsp.user?.userName,
      linkedAccountState
    };

    if (rsp.refreshToken && rsp.refreshToken !== '') {
      authState.refreshToken = rsp.refreshToken;
    }
    else {
      // use the current one.
      authState.refreshToken = this.authState?.refreshToken;
    }

    if (rsp.accessToken && rsp.accessToken !== '') {
      authState.accessToken = rsp.accessToken;
    }
    else {
      // use the current one.
      authState.accessToken = this.authState?.accessToken;
    }

    if (rsp.microstrategyIdToken) {
      authState.msBiState = new MicrostrategyBiAuthState(rsp.microstrategyIdToken);
    }

    this.setAuthState(authState);
    if (rsp.account) {
      this.cache.set(CACHEKEY_ACCOUNT, rsp.account, 600_000);
    }
    if (rsp.user) {
      this.cache.set(CACHEKEY_USER, rsp.user, 600_000);
    }

    if (rsp.badgeCount) {
      BadgeData.doSums(rsp.badgeCount);
      this.cache.set(BADGECOUNTS, rsp.badgeCount);
    }

    if (typeof (pendo) === 'object') {
      if (!pendo.isReady()) {
        pendo.initialize(pendoInfo);
      } else {
        pendo.updateOptions(pendoInfo);
      }
    }

  }

  public exchangeRefreshToken(username: string, forAccess = false): Observable<string> {
    this.logger.trace('exchange refresh token ' + forAccess);
    const url = `${this.environment.base}/public/sec/refresh`;

    if (!this.refreshObs) {
      this.sm.nobusy = true;
      this.refreshObs = this.http.post<RefreshResponse>(url, { refreshToken: this.authState?.refreshToken }, { withCredentials: false, reportProgress: true });

    }

    return this.refreshObs.pipe(tap(rsp => {
      this.logger.trace('got response to refresh ' + JSON.stringify(rsp));
      this.refreshObs = undefined;
      if (rsp.status === 'success') {
        const as = this.getAuthState();
        if (as) {
          as.accessToken = rsp.accessToken;
          as.idToken = rsp.idToken;
          if (rsp.refreshToken) {
            as.refreshToken = rsp.refreshToken;
          }
          this.setAuthState(as);
        }
      }
    },
      error => {
        this.logger.debug('error exchanging refresh token for new access token');
        this.logger.debug(error);
        this.setAuthState({ state: 'signedOut', stateQualifier: 'tokenExpiration' });
        this.cache.clear();

        // save where we are at so that post login, we can return as best we can.
        // this.router.
        this.redirectUrl = this.router.routerState?.snapshot?.url;

        this.router.navigate(['/sec/login']);
      }),
      map(rsp => {
        if (forAccess) {
          return rsp.accessToken;
        } else {
          return rsp.idToken;
        }
      }));
  }

  public getIdToken(checkExpiration = true): Promise<string | undefined> {
    if ((!this.authState) || (!this.authState.idToken)) {
      return Promise.resolve(undefined);
    }
    if (checkExpiration && (this.jwtHelper.isTokenExpired(this.authState.idToken))) {
      //this.logger.debug('getIdToken - token has expired.');
      this.logger.trace('getIdToken - token has expired');
      // if I have a refresh token, use it to fetch an update.
      if (this.authState.refreshToken && this.authState.username) {
        return this.exchangeRefreshToken(this.authState.username).toPromise();
      }
      else {
        return Promise.resolve(undefined);
      }
    }
    else {
      return Promise.resolve(this.authState.idToken);
    }
  }

  // used for changed password screen
  public getAccessToken(checkExpiration = true): Promise<string | undefined> {
    if ((!this.authState) || (!this.authState.accessToken)) {
      return Promise.resolve(undefined);
    }
    if (checkExpiration && (this.jwtHelper.isTokenExpired(this.authState.accessToken))) {
      // if I have a refresh token, use it to fetch an update.
      this.logger.debug('getAccessToken - token has expired.');
      if (this.authState.refreshToken && this.authState.username) {
        return this.exchangeRefreshToken(this.authState.username, true).toPromise();
      }
      else {
        return Promise.resolve(undefined);
      }
    }
    else {
      return Promise.resolve(this.authState.accessToken);
    }
  }

  public getRefreshToken(): string | undefined {
    return this.authState?.refreshToken;
  }



  /**
   * Do whatever is required (route user) to login page.
   */
  async logout(displayTokenExpired = false): Promise<boolean> {

    // if no auth state or id token, user not logged in so return success.
    if ((!this.authState) || (!this.authState.idToken)) {
      return Promise.resolve(true);
    }

    // if user does have an id token but it has expired, we
    // don't log out via API since they have no valid session and it will fail.
    // Just return true.
    if (this.jwtHelper.isTokenExpired(this.authState.idToken)) {
      if (displayTokenExpired) {
        this.setAuthState({ state: 'signedOut', stateQualifier: 'tokenExpiration' });
      }
      else {
        this.setAuthState({ state: 'signedOut' });
      }
      this.cache.clear();
      return Promise.resolve(true);
    }

    const url = `${this.environment.base}/secure/sec/logout`;

    // if user has bi state and has opened a session, send it
    // to the back end for disposal.
    const body = {
      refreshToken: this.authState.refreshToken,
      biSessionState: this.authState.msBiState?.sessionState
    };

    return firstValueFrom(this.http.post<IScrBaseResponse>(url, body, WITHCRED).pipe(
      tap(rsp => {
        if (rsp.status === 'success') {
          this.setAuthState({ state: 'signedOut' });
          this.cache.clear();
        }
      }),
      map(rsp => {
        this.logger.trace('logout response ' + JSON.stringify(rsp));
        return true;
      })));
  }

  public async getLinkedAccountState(): Promise<LinkedAccountState | undefined> {
    return this.authState?.linkedAccountState;
  }

  public async restoreLinkedAccountManagerState(): Promise<boolean> {

    return new Promise<boolean>((res) => {
      if (!this.authState?.linkedAccountState?.managerIdToken) {
        this.sm.emitChange({
          type: 'error', body: 'Please log out and log back in as usual.', title: 'Return Failed', autoclear: true
        });
        res(false);
      }
      else {
        const url = `${this.environment.base}/public/sec/refresh`;
        // restore the refresh token here as the response using it does not provide a new one.
        this.authState.refreshToken = this.authState.linkedAccountState.managerRefreshToken;
        const body = {
          refreshToken: this.authState.linkedAccountState.managerRefreshToken
        };
        this.http.post<LoginResponse>(url, body, { withCredentials: false, reportProgress: true }).subscribe({
          next: (rsp: LoginResponse) => {
            // console.trace('restore manager login response ' + JSON.stringify(rsp));
            if (rsp.status === 'success') {
              this.processLoginSuccessResponse(rsp, this.authState?.linkedAccountState?.managerUserName);
              res(true);
            }
            else if (rsp.messages && rsp.messages.length > 0) {
              this.sm.emitChange({ type: 'error', body: rsp.messages[0].message, title: 'Return Failed', autoclear: true });
            }
          },
          error: (e) => {
            this.sm.emitChange({ type: 'error', body: e.toString(), title: 'Return Failed', autoclear: true });
            console.error(e);
            res(false);
          }
        });
      }
    });
  }

  public getLinkedAccountPermissionForAccount(accountId: number): Observable<LoginResponse> {
    const url = `${this.environment.base}/secure2/account/linked`;
    const req = {
      accountId
    };
    return this.http.post<LoginResponse>(url, req, WithCredNoCache).pipe(tap(rsp => {
      if (rsp.status === 'success') {

        const username = this.getScrUserName();
        const managingAccountId = this.authState?.pendoInfo?.account.id;
        const linkedAccountState = new LinkedAccountState();
        linkedAccountState.currentUser = rsp.user?.userName ?? undefined;
        linkedAccountState.linkedAccountId = rsp.account?.accountId ?? undefined;
        linkedAccountState.linkedAccountName = rsp.account?.linkedAccountName;
        linkedAccountState.managingAccountId = managingAccountId;

        // preserve the existing permissions so that we can return.
        linkedAccountState.managerAccessToken = this.authState?.accessToken;
        linkedAccountState.managerIdToken = this.authState?.idToken;
        linkedAccountState.managerRefreshToken = this.authState?.refreshToken;
        linkedAccountState.managerUserName = this.authState?.username;

        this.processLoginSuccessResponse(rsp, username ?? '', linkedAccountState);
      }
    }));



  }

}
